// URL <-> state helpers for the milestone tab. Mirrors the deliverable
// tab's bracket-notation pattern (deliverableUrlState.ts) so the two
// tabs read alike for users who switch between them. Filter / search /
// pagination round-trip via:
//
//   ?filters[status]=delivered,partially-accepted
//   ?filters[awaitingAcceptance]=1
//   ?filters[archived]=1
//   ?q=search
//   ?page=2&pageSize=50
//
// Empty / default values are stripped from the URL so `/?page=1` and
// `/` look identical to the user. Sort is MilestoneTab's own
// component-local state (not URL state) — `sort`/`dir` are kept in the
// owned-key deletion list purely to scrub stale values from URLs
// bookmarked before that field moved out of the URL.

import type { MilestoneDerivedStatus } from "../../../../utils/deriveMilestoneStatus";
import {
  ALL_SCOPE,
  parseScope,
  serialiseScope,
  type ScopeValue,
} from "../shared/scopeTypes";

/** Synthetic token in `sow` matching milestones with `sowId == null`.
 *  Same convention as the deliverable FilterChipBar. */
export const UNATTACHED_TOKEN = "unattached" as const;

export interface MilestoneFilterChipBarFilters {
  status: MilestoneDerivedStatus[];
  awaitingAcceptance: boolean;
  /** SOW attachment filter. Carries SOW ids and/or the synthetic
   *  `UNATTACHED_TOKEN` for unattached milestones. Empty array =
   *  no SOW filter active. */
  sow: string[];
  includeArchived: boolean;
}

export type MilestoneTableView = "grouped" | "sequential";

export const TIMELINE_WINDOW_STEPS = [30, 60, 90, 180] as const;
export type TimelineWindowDays = (typeof TIMELINE_WINDOW_STEPS)[number];
export const DEFAULT_TIMELINE_WINDOW: TimelineWindowDays = 90;

export interface MilestoneUrlState {
  filters: MilestoneFilterChipBarFilters;
  search: string;
  page: number;
  pageSize: number;
  scope: ScopeValue;
  view: MilestoneTableView;
  /** Active-window size for the dual-tier timeline. Default 90. */
  timelineWindow: TimelineWindowDays;
  /** Centre date for the active window (YYYY-MM-DD). Null = today-centred. */
  timelineFocus: string | null;
}

export const MILESTONE_DEFAULT_PAGE_SIZE = 25;

const STATUS_VALUES = new Set<MilestoneDerivedStatus>([
  "upcoming",
  "delivered",
  "partially-accepted",
  "accepted",
  "paid",
]);

function parseList<T extends string>(
  raw: string | null,
  allowed: Set<T>,
): T[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s): s is T => (allowed as Set<string>).has(s));
}

// SOW ids are dynamic, so the parser is a passthrough — synthetic
// UNATTACHED_TOKEN and any backend SOW id round-trip; unknown tokens
// sit harmlessly in state until the user clears the chip.
function parseSowList(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function parseMilestoneUrlState(
  params: URLSearchParams,
): MilestoneUrlState {
  const filters: MilestoneFilterChipBarFilters = {
    status: parseList(params.get("filters[status]"), STATUS_VALUES),
    awaitingAcceptance:
      params.get("filters[awaitingAcceptance]") === "1",
    sow: parseSowList(params.get("filters[sow]")),
    includeArchived: params.get("filters[archived]") === "1",
  };
  const search = params.get("q") ?? "";
  const pageRaw = Number(params.get("page") ?? "1");
  const page =
    Number.isFinite(pageRaw) && pageRaw > 0 ? Math.floor(pageRaw) : 1;
  const pageSizeRaw = Number(
    params.get("pageSize") ?? MILESTONE_DEFAULT_PAGE_SIZE,
  );
  const pageSize =
    Number.isFinite(pageSizeRaw) && pageSizeRaw > 0
      ? Math.floor(pageSizeRaw)
      : MILESTONE_DEFAULT_PAGE_SIZE;
  const viewRaw = params.get("view");
  const view: MilestoneTableView =
    viewRaw === "grouped" ? "grouped" : "sequential";
  const windowRaw = Number(params.get("window"));
  const timelineWindow: TimelineWindowDays = (
    TIMELINE_WINDOW_STEPS as ReadonlyArray<number>
  ).includes(windowRaw)
    ? (windowRaw as TimelineWindowDays)
    : DEFAULT_TIMELINE_WINDOW;
  const focusRaw = params.get("focus");
  const timelineFocus =
    typeof focusRaw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(focusRaw)
      ? focusRaw
      : null;
  return {
    filters,
    search,
    page,
    pageSize,
    scope: parseScope(params.get("scope")),
    view,
    timelineWindow,
    timelineFocus,
  };
}

export function serialiseMilestoneUrlState(
  state: MilestoneUrlState,
  base?: URLSearchParams,
): URLSearchParams {
  // Preserve any unrelated params (e.g. ?tab=milestone, ?item=...).
  const next = new URLSearchParams(base?.toString() ?? "");
  // Reset the keys this state owns.
  const owned = [
    "filters[status]",
    "filters[awaitingAcceptance]",
    "filters[sow]",
    "filters[archived]",
    "q",
    // Sort moved out of URL state (MilestoneTab's sort is component-local);
    // kept here only to scrub stale values from bookmarked pre-change URLs.
    "sort",
    "dir",
    "page",
    "pageSize",
    "scope",
    "view",
    "window",
    "focus",
  ];
  for (const k of owned) next.delete(k);
  if (state.filters.status.length) {
    next.set("filters[status]", state.filters.status.join(","));
  }
  if (state.filters.awaitingAcceptance) {
    next.set("filters[awaitingAcceptance]", "1");
  }
  if (state.filters.sow.length) {
    next.set("filters[sow]", state.filters.sow.join(","));
  }
  if (state.filters.includeArchived) next.set("filters[archived]", "1");
  if (state.search.trim()) next.set("q", state.search);
  if (state.page > 1) next.set("page", String(state.page));
  if (state.pageSize !== MILESTONE_DEFAULT_PAGE_SIZE) {
    next.set("pageSize", String(state.pageSize));
  }
  const scopeRaw = serialiseScope(state.scope);
  if (scopeRaw !== null) next.set("scope", scopeRaw);
  if (state.view === "grouped") next.set("view", "grouped");
  if (state.timelineWindow !== DEFAULT_TIMELINE_WINDOW) {
    next.set("window", String(state.timelineWindow));
  }
  if (state.timelineFocus) next.set("focus", state.timelineFocus);
  return next;
}

export function emptyMilestoneUrlState(): MilestoneUrlState {
  return {
    filters: {
      status: [],
      awaitingAcceptance: false,
      sow: [],
      includeArchived: false,
    },
    search: "",
    page: 1,
    pageSize: MILESTONE_DEFAULT_PAGE_SIZE,
    scope: ALL_SCOPE,
    view: "sequential",
    timelineWindow: DEFAULT_TIMELINE_WINDOW,
    timelineFocus: null,
  };
}
